/**
 * PostHog client setup.
 *
 * Initialized once at app boot via initPostHog(). Every helper is a safe
 * no-op when no API key is configured so local dev and tests don't ship
 * events.
 *
 * Session replay is enabled with aggressive defaults:
 *   - All input fields are masked (resumes contain PII)
 *   - All text is masked unless a parent is opted in with `data-ph-no-mask`
 *   - Sampled at 100% — flip to a lower rate from the PostHog dashboard
 *     under Project Settings > Recordings if volume becomes an issue.
 */

import posthog from 'posthog-js';
import type { CaptureResult } from 'posthog-js';

const API_KEY = import.meta.env.VITE_POSTHOG_KEY || '';
const HOST = import.meta.env.VITE_POSTHOG_HOST || 'https://us.i.posthog.com';

let initialized = false;

// ---------------------------------------------------------------------------
// Capability-token scrubbing
//
// Several routes carry secret capability tokens in the URL (shadow-claim,
// invite signup, email verification, public resume links). Those secrets must
// never reach a third-party analytics processor, so every URL-ish property is
// scrubbed before events leave the browser.
// ---------------------------------------------------------------------------

/** Token-in-path routes: /claim/:token, /invite/:token, /verify-email/:token, /resume/:accessToken */
const TOKEN_PATH_RE = /(\/(?:claim|invite|verify-email|resume))\/[^/?#]+/;

/** Query params that carry capability tokens (e.g. /signup?invite=<token>). */
const SENSITIVE_QUERY_PARAMS = ['invite', 'token'];

/** Redact the token segment of a token-bearing pathname. */
export function scrubPathname(pathname: string): string {
  return pathname.replace(TOKEN_PATH_RE, '$1/:token');
}

/** Redact sensitive query params from a search string ('?a=b' or 'a=b'). */
export function scrubSearch(search: string): string {
  if (!search) return search;
  const hadPrefix = search.startsWith('?');
  const params = new URLSearchParams(hadPrefix ? search.slice(1) : search);
  let changed = false;
  for (const key of SENSITIVE_QUERY_PARAMS) {
    if (params.has(key)) {
      params.set(key, 'redacted');
      changed = true;
    }
  }
  if (!changed) return search;
  const result = params.toString();
  return hadPrefix ? `?${result}` : result;
}

/** Scrub a full URL or an app-relative path (pathname + search). */
function scrubUrlLike(value: string): string {
  try {
    const url = new URL(value);
    url.pathname = scrubPathname(url.pathname);
    url.search = scrubSearch(url.search);
    return url.toString();
  } catch {
    // Not an absolute URL: treat as pathname(+search)
    const [pathAndQuery, hash] = value.split('#', 2);
    const [path, query] = pathAndQuery.split('?', 2);
    let result = scrubPathname(path);
    if (query !== undefined) result += `?${scrubSearch(query)}`;
    if (hash !== undefined) result += `#${hash}`;
    return result;
  }
}

/** Event property keys that carry URLs or paths. */
const URL_PROPERTY_KEYS = [
  '$current_url',
  '$referrer',
  '$pathname',
  '$prev_pageview_pathname',
  '$initial_current_url',
  '$initial_referrer',
  '$session_entry_url',
  'search',
];

function scrubRecord(record: Record<string, unknown> | undefined): void {
  if (!record) return;
  for (const key of URL_PROPERTY_KEYS) {
    const value = record[key];
    if (typeof value === 'string' && value) {
      record[key] = scrubUrlLike(value);
    }
  }
}

/** before_send hook: scrub capability tokens from every outgoing event. */
function scrubEvent(event: CaptureResult | null): CaptureResult | null {
  if (!event) return event;
  scrubRecord(event.properties as Record<string, unknown> | undefined);
  scrubRecord(event.$set as Record<string, unknown> | undefined);
  scrubRecord(event.$set_once as Record<string, unknown> | undefined);
  return event;
}

export function initPostHog(): void {
  if (initialized) return;
  initialized = true;

  if (!API_KEY) {
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.info('[posthog] no VITE_POSTHOG_KEY set — analytics disabled');
    }
    return;
  }

  posthog.init(API_KEY, {
    api_host: HOST,
    // Autocapture: every click, form submit, input change, pageleave.
    autocapture: true,
    capture_pageview: false, // we route manually via usePageTracking
    capture_pageleave: true,

    // Session replay with PII masking on by default.
    session_recording: {
      maskAllInputs: true,
      maskTextSelector: '*',
      // Anything inside an element with `data-ph-no-mask` will be visible in
      // recordings — use sparingly for UI chrome that's never sensitive.
    },
    disable_session_recording: false,

    // Cross-subdomain persistence so app.careercatalyst.cc and the marketing
    // domain share the same anonymous → identified user lineage.
    persistence: 'localStorage+cookie',
    cross_subdomain_cookie: true,

    // Respect Do Not Track.
    respect_dnt: true,

    // Capability tokens (claim/invite/verify/resume links) must never reach
    // PostHog — scrub every URL-ish property on every event.
    before_send: scrubEvent,

    loaded: (ph) => {
      if (import.meta.env.DEV) {
        // Keep dev events out of production projects unless explicitly opted in.
        const optIn = import.meta.env.VITE_POSTHOG_DEV_OPT_IN === 'true';
        if (!optIn) ph.opt_out_capturing();
      }
    },
  });
}

/** Identify a logged-in user. Safe to call repeatedly. */
export function identifyUser(
  distinctId: string,
  properties: Record<string, unknown> = {}
): void {
  if (!initialized || !API_KEY) return;
  try {
    posthog.identify(distinctId, properties);
  } catch {
    // Silent — never let analytics break the app.
  }
}

/** Track a custom business event. */
export function trackEvent(
  event: string,
  properties: Record<string, unknown> = {}
): void {
  if (!initialized || !API_KEY) return;
  try {
    posthog.capture(event, { $source: 'frontend', ...properties });
  } catch {
    // Silent.
  }
}

/** Track a pageview manually (we own routing via React Router). */
export function trackPageview(
  pathname: string,
  properties: Record<string, unknown> = {}
): void {
  if (!initialized || !API_KEY) return;
  try {
    const safeProperties = { ...properties };
    if (typeof safeProperties.search === 'string') {
      safeProperties.search = scrubSearch(safeProperties.search);
    }
    posthog.capture('$pageview', {
      $pathname: scrubPathname(pathname),
      ...safeProperties,
    });
  } catch {
    // Silent.
  }
}

/** Clear all identity on logout. */
export function resetPostHog(): void {
  if (!initialized || !API_KEY) return;
  try {
    posthog.reset();
  } catch {
    // Silent.
  }
}

export { posthog };
